import type { ReactNode } from 'react';

/**
 * The model anchors findings inline with `[block:<id>]` / `[section:<id>]`
 * markers (emitted by the contract serializer's block/section headers). Those
 * raw markers are useless to a reviewer — this turns them into readable,
 * clickable references:
 *   - `[block:<id>]`  → a chip showing the block's name that scrolls to and
 *                       highlights the block on click (reusing the existing
 *                       jump-to-block flow via `onBlockClick`).
 *   - `[section:<id>]`→ a non-interactive chip showing the section's name
 *                       (there is no scroll-to-section flow today).
 * Unknown ids fall back to a generic "block" / "section" label so we never
 * surface a raw UUID.
 */

// Matches `[block:<id>]` and `[section:<id>]`. The id group is permissive
// (uuid today, but we don't want to silently fail if the id format changes).
const REF_PATTERN = /\[(block|section):([0-9a-zA-Z_-]+)\]/g;

export interface BlockRefOptions {
  blockNameById: Map<string, string>;
  sectionNameById?: Map<string, string>;
  onBlockClick: (blockId: string) => void;
}

// Outlined and colourless, matching the go-to-block chip on a finding card's
// meta line: inside the card, only the severity pill carries colour.
const chipBase =
  'mx-0.5 inline-flex max-w-full items-center truncate rounded-full border px-1.5 py-px text-[11px] align-baseline transition-colors';

/**
 * Plain-text twin of `renderTextWithBlockRefs` for places that cannot hold
 * elements — the reason textarea prefilled with the AI's rationale. Markers
 * become the block / section name in quotes, never a raw id.
 */
export const replaceBlockRefsWithNames = (
  text: string,
  opts: Pick<BlockRefOptions, 'blockNameById' | 'sectionNameById'>,
): string =>
  text.replace(REF_PATTERN, (_token, kind: string, id: string) => {
    const name =
      kind === 'block' ? blockNameOrFallback(opts.blockNameById, id) : (opts.sectionNameById?.get(id) ?? 'section');
    return `“${name}”`;
  });

const blockNameOrFallback = (blockNameById: Map<string, string>, id: string) => blockNameById.get(id) ?? 'block';

export const renderTextWithBlockRefs = (text: string, opts: BlockRefOptions): ReactNode[] => {
  const { blockNameById, sectionNameById, onBlockClick } = opts;
  const nodes: ReactNode[] = [];
  let lastIndex = 0;
  let key = 0;

  // `matchAll` with a global regex yields each marker with its index, letting
  // us slice out the plain-text runs between markers.
  for (const match of text.matchAll(REF_PATTERN)) {
    const [token, kind, id] = match;
    const start = match.index ?? 0;

    if (start > lastIndex) {
      nodes.push(text.slice(lastIndex, start));
    }

    if (kind === 'block') {
      const name = blockNameById.get(id) ?? 'block';
      nodes.push(
        <button
          key={`ref-${key++}`}
          type="button"
          onClick={() => onBlockClick(id)}
          title="Scroll to this block"
          className={`${chipBase} cursor-pointer border-gray-300 text-gray-600 hover:border-gray-400 hover:text-gray-900`}
        >
          {name}
        </button>,
      );
    } else {
      const name = sectionNameById?.get(id) ?? 'section';
      nodes.push(
        <span key={`ref-${key++}`} className={`${chipBase} border-gray-200 text-gray-500`} title="Contract section">
          {name}
        </span>,
      );
    }

    lastIndex = start + token.length;
  }

  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes;
};
